'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ScrollIndicatorProps {
  targetId?: string;
  label?: string;
  labelDe?: string;
}

export default function ScrollIndicator({ targetId, label = 'Scroll to explore', labelDe = 'Weiter scrollen' }: ScrollIndicatorProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);
  const [currentLang, setCurrentLang] = useState('en');
  const [hasContentBehind, setHasContentBehind] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    
    // Listen for language changes
    const handleLanguageChange = () => {
      const htmlLang = document.documentElement.lang || 'en';
      setCurrentLang(htmlLang);
    };
    
    handleLanguageChange();
    
    // Set up observer for language changes
    const observer = new MutationObserver(handleLanguageChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
    });

    // Check if there's content below the fold AND if there's content behind the indicator
    const checkContentBelow = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      
      // Show indicator if there's more content below (at least 200px)
      const hasMoreContent = documentHeight - (windowHeight + scrollTop) > 200;
      
      // Check if there's any content element behind the indicator position
      const indicatorBottom = windowHeight - 64; // bottom-8 = 32px from bottom, plus height
      const elementsAtBottom = document.elementsFromPoint(
        window.innerWidth / 2,
        indicatorBottom
      );
      
      // Filter out the indicator itself and check if there's actual content
      const hasContent = elementsAtBottom.some(el => {
        // Ignore the indicator itself, html, body, and background elements
        if (
          el.tagName === 'HTML' ||
          el.tagName === 'BODY'|| el.classList.contains('scroll-indicator') ||
          el.closest('button[aria-label="Scroll to next section"]') ||
          el.id === 'particle-background'|| el.classList.contains('particle-background')
        ) {
          return false;
        }
        
        // Check if element has actual content or is a content container
        const hasTextContent = el.textContent && el.textContent.trim().length > 0;
        const isContentElement = el.tagName === 'SECTION' || 
                                 el.tagName === 'DIV' || 
                                 el.tagName === 'ARTICLE' ||
                                 el.tagName === 'MAIN';
        
        return hasTextContent || isContentElement;
      });
      
      setShowIndicator(hasMoreContent);
      setHasContentBehind(hasContent);
    };

    checkContentBelow();
    window.addEventListener('scroll', checkContentBelow);
    window.addEventListener('resize', checkContentBelow);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', checkContentBelow);
      window.removeEventListener('resize', checkContentBelow);
    };
  }, []);

  const handleScrollClick = () => {
    if (!isHydrated) return;
    
    if (targetId) {
      const targetSection = document.getElementById(targetId);
      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    
    // Default: scroll down one viewport height
    window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
  };

  // Hide if not hydrated, no more content, or content is behind the indicator
  if (!isHydrated || !showIndicator || hasContentBehind) {
    return null;
  }

  const displayLabel = currentLang === 'de' ? labelDe : label;

  return (
    <button
      onClick={handleScrollClick}
      className="scroll-indicator fixed bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-2 cursor-pointer group z-50 transition-opacity duration-300"
      aria-label="Scroll to next section"
      style={{
        opacity: showIndicator && !hasContentBehind ? 1 : 0,
        pointerEvents: showIndicator && !hasContentBehind ? 'auto' : 'none'
      }}
    >
      <span 
        className="text-sm group-hover:text-accent transition-colors duration-300"
        style={{ 
          fontFamily: '"Inter", sans-serif',
          color: '#ffffff'
        }}
      >
        {displayLabel}
      </span>
      <Icon
        name="ChevronDownIcon"
        size={24}
        variant="outline"
        className="animate-bounce group-hover:text-highlight transition-colors duration-300"
        style={{ color: '#00b8ff' }}
      />
    </button>
  );
}